import { Component, DestroyRef, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '@app/sharedModule/components/confirmation-dialog/confirmation-dialog.component';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { SmartCartListItem, SmartCartService } from '../smart-cart.service';

type SmartCartMode = 'live' | 'disabled';

interface SmartCartListRow {
  cart: SmartCartListItem;
  stateControl: FormControl<SmartCartMode>;
}

enum Actions {
  EDIT = 'edit',
  DUPLICATE = 'duplicate',
  DELETE = 'delete',
}

const ActionOptions = [
  { label: 'Edit', value: Actions.EDIT },
  { label: 'Duplicate', value: Actions.DUPLICATE },
  { label: 'Delete', value: Actions.DELETE },
];

@Component({
  selector: 'smart-cart-list',
  templateUrl: './smart-cart-list.component.html',
  styleUrls: ['./smart-cart-list.component.scss'],
  standalone: false,
})
export class SmartCartListComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Output() liveStateChanged = new EventEmitter<void>();

  rows: SmartCartListRow[] = [];
  loading = false;
  // Config being duplicated (server-side provisioning is slow) — drives the spinner + blocks concurrent duplicates.
  duplicatingId: number | null = null;
  actionOptions = ActionOptions;
  readonly Actions = Actions;

  constructor(
    private smartCartService: SmartCartService,
    private toastService: ToastService,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  ngOnInit() {
    this.loadSmartCarts();
  }

  onActionOptionSelect(optionValue: Actions, cartId: number) {
    switch (optionValue) {
      case Actions.EDIT: {
        this.router.navigate(['/engage/smart-cart', cartId]);
        break;
      }
      case Actions.DELETE: {
        this.confirmDelete(cartId);
        break;
      }
      case Actions.DUPLICATE: {
        this.duplicateCart(cartId);
        break;
      }
    }
  }

  private duplicateCart(cartId: number) {
    if (this.duplicatingId !== null) return;
    this.duplicatingId = cartId;
    this.smartCartService
      .duplicate(cartId)
      .pipe(
        finalize(() => (this.duplicatingId = null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.toastService.showToast({ mode: 'success', message: 'Smart Cart duplicated successfully' });
          this.loadSmartCarts();
        },
        error: (err) => {
          console.error('Failed to duplicate smart cart:', err);
          this.toastService.showToast({ mode: 'danger', message: 'Failed to duplicate Smart Cart' });
        },
      });
  }

  private confirmDelete(cartId: number) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Smart Cart',
          description: 'Are you sure you want to delete this Smart Cart?',
          confirmText: 'Delete',
          cancelText: 'Cancel',
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.smartCartService.delete(cartId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          const wasLive = this.rows.find((row) => row.cart.id === cartId)?.cart.isLive ?? false;
          this.rows = this.rows.filter((row) => row.cart.id !== cartId);
          this.toastService.showToast({ mode: 'success', message: 'Smart Cart deleted successfully' });
          if (wasLive) this.liveStateChanged.emit();
        },
        error: (err) => {
          console.error('Failed to delete smart cart:', err);
          this.toastService.showToast({ mode: 'danger', message: 'Failed to delete Smart Cart' });
        },
      });
  }

  private loadSmartCarts() {
    this.loading = true;
    this.smartCartService
      .list()
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (smartCarts) => {
          this.rows = (smartCarts ?? []).map((cart) => this.buildRow(cart));
        },
        error: (err) => {
          console.error('Error loading smart cart settings:', err);
          this.toastService.showToast({ mode: 'danger', message: 'Failed to load smart cart settings' });
          this.rows = [];
        },
      });
  }

  private buildRow(cart: SmartCartListItem): SmartCartListRow {
    const initial: SmartCartMode = cart.isLive ? 'live' : 'disabled';
    const stateControl = new FormControl<SmartCartMode>(initial, { nonNullable: true });
    const row: SmartCartListRow = { cart, stateControl };

    stateControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((nextValue) => {
      const currentValue: SmartCartMode = row.cart.isLive ? 'live' : 'disabled';
      if (nextValue === currentValue) return;
      this.confirmStateChange(row, nextValue, currentValue);
    });

    return row;
  }

  private confirmStateChange(row: SmartCartListRow, requestedValue: SmartCartMode, previousValue: SmartCartMode) {
    const isActivating = requestedValue === 'live';
    const description = isActivating
      ? 'Only one Smart Cart can be live.\nYour current live Smart Cart will be turned off automatically.'
      : 'This smart cart will be turned off.';
    const confirmText = isActivating ? 'Go Live' : 'Confirm';

    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Are you sure?',
          description,
          confirmText,
          cancelText: 'Cancel',
        },
      })
      .afterClosed()
      .pipe(
        tap((confirmed) => {
          if (!confirmed) {
            row.stateControl.setValue(previousValue);
          }
        }),
        filter(Boolean),
        switchMap(() => this.runStateChange(row, requestedValue)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          row.cart.isLive = isActivating;
          if (isActivating) {
            this.demoteOtherRowsLocally(row.cart.id);
          }
          this.toastService.showToast({
            mode: 'success',
            message: isActivating ? 'Smart Cart is now live' : 'Smart Cart disabled',
          });
          this.liveStateChanged.emit();
        },
        error: (err) => {
          console.error('Failed to change smart cart state:', err);
          this.toastService.showToast({ mode: 'danger', message: 'Failed to change Smart Cart state' });
          row.stateControl.setValue(previousValue);
        },
      });
  }

  private runStateChange(row: SmartCartListRow, nextValue: SmartCartMode): Observable<SmartCartListItem> {
    return nextValue === 'live'
      ? this.smartCartService.activate(row.cart.id)
      : this.smartCartService.deactivate(row.cart.id);
  }

  private demoteOtherRowsLocally(activatedId: number) {
    this.rows.forEach((otherRow) => {
      if (otherRow.cart.id === activatedId) return;
      if (!otherRow.cart.isLive) return;
      otherRow.cart.isLive = false;
      otherRow.stateControl.setValue('disabled');
    });
  }
}
